import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import SecureVideoPlayer from './components/SecureVideoPlayer';
import PixelSnow from './components/PixelSnow';
import FloatingCart from './components/FloatingCart';
import Navbar from './components/Navbar';
import './CourseDetailsPage.css';
import './CoursesPage.css';

export default function CourseDetailsPage() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [expandedModules, setExpandedModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLecture, setActiveLecture] = useState(null);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState(null);
  const [videoAccessError, setVideoAccessError] = useState(null);
  const navigate = useNavigate();

  const handleAddToCart = () => {
    if (!course) return;
    const cart = JSON.parse(localStorage.getItem('engimitra_cart') || '[]');
    const newItem = {
      id: course.id,
      item_type: 'course',
      title: course.title,
      thumbnail_url: course.thumbnail_url,
      price: course.price,
      original_price: course.original_price
    };
    if (!cart.some(item => item.id === course.id)) {
      cart.push(newItem);
      localStorage.setItem('engimitra_cart', JSON.stringify(cart));
    }
    navigate('/cart');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCourseDetails();
    checkPurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Resolve the actual playable video URL whenever the active lecture changes.
  // video_url is never fetched into module/lecture state (see fetchCourseDetails)
  // — it's resolved here via the entitlement-checked API, which allows it
  // through only if the lecture's module is unlocked or the course is purchased.
  useEffect(() => {
    setActiveVideoUrl(null);
    setVideoAccessError(null);
    if (!activeLecture || activeLecture.type === 'document') return;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = session ? { Authorization: `Bearer ${session.access_token}` } : {};
        const res = await fetch(`/api/get-signed-url?itemType=lecture&itemId=${encodeURIComponent(activeLecture.id)}&variant=full`, { headers });
        const data = await res.json();
        if (res.ok) {
          setActiveVideoUrl(data.signedUrl);
        } else {
          setVideoAccessError(data.error || 'Unable to load this lecture');
        }
      } catch (err) {
        console.error('Error fetching lecture video:', err);
        setVideoAccessError('Unable to load this lecture');
      }
    })();
  }, [activeLecture]);

  const checkPurchase = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('purchases').select('id')
      .eq('user_id', user.id).eq('item_id', id).eq('item_type', 'course').single();
    if (data) setHasPurchased(true);
  };

  const openLectureDocument = async (lec) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = session ? { Authorization: `Bearer ${session.access_token}` } : {};
      const res = await fetch(`/api/get-signed-url?itemType=lecture&itemId=${encodeURIComponent(lec.id)}&variant=full`, { headers });
      const data = await res.json();
      if (res.ok && data.signedUrl) window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error opening lecture document:', err);
    }
  };

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('id', id)
        .single();

      if (courseError) throw courseError;
      setCourse(courseData);

      // Fetch modules and nested lectures. video_url is deliberately excluded —
      // it's the paid asset and must never appear in an unauthenticated select;
      // it's resolved per-lecture, post-entitlement-check, via /api/get-signed-url.
      const { data: moduleData, error: moduleError } = await supabase
        .from('modules')
        .select(`*, lectures(id, title, duration, lecture_number, type, module_id)`)
        .eq('course_id', id)
        .order('module_number', { ascending: true });

      if (!moduleError && moduleData) {
        const sorted = moduleData.map(mod => ({
          ...mod,
          lectures: (mod.lectures || []).sort((a, b) => a.lecture_number - b.lecture_number)
        }));
        setModules(sorted);
        if (sorted.length > 0) {
          setExpandedModules([sorted[0].id]);
          // Set first lecture of first module as active by default
          if (sorted[0].lectures && sorted[0].lectures.length > 0) {
            setActiveLecture(sorted[0].lectures[0]);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching course:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (modId) => {
    setExpandedModules(prev =>
      prev.includes(modId) ? prev.filter(mid => mid !== modId) : [...prev, modId]
    );
  };

  const totalLectures = modules.reduce((acc, mod) => acc + (mod.lectures?.length || 0), 0);

  if (loading) {
    return (
      <div className="cdp-container">
        <Navbar />
        <div className="cdp-skeleton-container">
          <div className="cdp-skel-left">
            <div className="cdp-skeleton cdp-skel-bc" />
            <div className="cdp-skeleton cdp-skel-hero" />
            <div className="cdp-skeleton cdp-skel-title" />
            <div className="cdp-skel-meta">
              <div className="cdp-skeleton cdp-skel-meta-item" />
              <div className="cdp-skeleton cdp-skel-meta-item" />
              <div className="cdp-skeleton cdp-skel-meta-item" />
            </div>
            <div className="cdp-skeleton cdp-skel-section" />
            <div className="cdp-skeleton cdp-skel-section" />
          </div>
          <div className="cdp-skel-right">
            <div className="cdp-skeleton cdp-skel-sidebar" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="cdp-container">
        <Navbar />
        <div className="cdp-error">
          <h1>Course Not Found</h1>
          <p>{error || "The course you are looking for does not exist or has been removed."}</p>
          <Link to="/courses" className="cp-btn" style={{ textDecoration: 'none' }}>Back to Courses</Link>
        </div>
      </div>
    );
  }

  const discount = course?.original_price && course?.price ? Math.round((1 - course.price / course.original_price) * 100) : null;

  return (
    <div className="cdp-container">
      {/* Dynamic Pixel Snow Background */}
      <div className="cdp-pixel-bg">
        <PixelSnow 
          color="#00e5c8"
          flakeSize={0.013}
          minFlakeSize={1.25}
          pixelResolution={230}
          speed={1.25}
          density={0.25}
          direction={125}
          brightness={0.7}
          variant="snowflake"
        />
      </div>
      {/* Navigation */}
      <Navbar />

      <main className="cdp-main">
        {/* Breadcrumb */}
        <div className="cdp-breadcrumb">
          <Link to="/">Home</Link>
          <span className="cdp-bc-sep">›</span>
          <Link to="/courses">Courses</Link>
          <span className="cdp-bc-sep">›</span>
          <span className="cdp-bc-current">{course.title}</span>
        </div>

        <div className="cdp-grid">
          {/* ---- Left Column ---- */}
          <div>
            {/* Now Playing Header (Moved out of frame) */}
            <div className="cdp-now-playing" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--cyan)', boxShadow: '0 0 10px var(--cyan)' }}></div>
              <span style={{ fontSize: '0.75rem', fontWeight: '800', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--cyan)' }}>
                {activeLecture ? `NOW WATCHING: ${activeLecture.title}` : 'Select a lecture to start'}
              </span>
            </div>

            {/* Hero Video Player (Secure) */}
            <div className="cdp-hero" style={{ aspectRatio: 'auto', border: 'none', background: 'transparent' }}>
              {activeLecture && activeLecture.type !== 'document' && activeVideoUrl ? (
                <SecureVideoPlayer
                  videoUrl={activeVideoUrl}
                  title={activeLecture.title}
                  thumbnailUrl={course.thumbnail_url}
                />
              ) : activeLecture && activeLecture.type !== 'document' && videoAccessError ? (
                <div style={{ position: 'relative', paddingBottom: '56.25%', background: 'var(--bg2)', borderRadius: '12px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '2rem', textAlign: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>{videoAccessError === 'You have not purchased this item' ? 'Purchase this course to watch this lecture.' : videoAccessError}</p>
                  </div>
                </div>
              ) : (
                <div style={{ position: 'relative', paddingBottom: '56.25%', background: 'var(--bg2)', borderRadius: '12px', overflow: 'hidden' }}>
                  {course.thumbnail_url ? (
                    <img alt={course.title} src={course.thumbnail_url} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    </div>
                  )}
                  <div className="cdp-hero-overlay">
                    <div className="cdp-play-circle">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Course Meta */}
            <div className="cdp-meta">
              <div className="cdp-tags">
                <span className="cdp-tag cdp-tag-cat">#{course.category || 'engineering'}</span>
                <span className="cdp-tag cdp-tag-level">#{course.level || 'all_levels'}</span>
              </div>
              <h1 className="cdp-course-title">
                <span className="cp-gradient-text">{course.title}</span>
              </h1>
              <div className="cdp-stats">
                <div className="cdp-stat">
                  <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                  <strong>{course.rating || '4.9'}</strong>
                  <span>(2,450 ratings)</span>
                </div>
                <div className="cdp-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  <span>15,200 Students</span>
                </div>
                <div className="cdp-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                  <span>{modules.length} Modules • {totalLectures} Lectures</span>
                </div>
              </div>
            </div>

            {/* About Section */}
            <section className="cdp-section">
              <h2 className="cdp-section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                About this course
              </h2>
              <p className="cdp-about-text">
                {course.description || `Master the complexities of ${course.title} using the ENGI<mitra> framework. This course bridges the gap between classical engineering principles and cutting-edge computational modeling.`}
              </p>
            </section>

            {/* Syllabus */}
            <section className="cdp-section">
              <div className="cdp-syllabus-header">
                <h2 className="cdp-section-title" style={{ margin: 0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
                  Syllabus
                </h2>
                <span className="cdp-syllabus-count">{modules.length} Modules • {totalLectures} Lectures</span>
              </div>

              {modules.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                  Syllabus is being updated. Please check back soon!
                </div>
              ) : (
                modules.map((mod) => {
                  const isExpanded = expandedModules.includes(mod.id);
                  const isLocked = mod.is_locked;
                  const modNum = mod.module_number < 10 ? `0${mod.module_number}` : mod.module_number;
                  const lectureCount = mod.lectures?.length || 0;

                  return (
                    <div key={mod.id} className={`cdp-module${isExpanded ? ' expanded' : ''}${isLocked ? ' locked' : ''}`}>
                      <div className="cdp-module-header" onClick={() => !isLocked && toggleModule(mod.id)}>
                        <div className="cdp-module-left">
                          <span className="cdp-module-num">{modNum}</span>
                          <div className="cdp-module-info">
                            <h3>{mod.title}</h3>
                            <span>{lectureCount} Lectures</span>
                          </div>
                        </div>
                        {isLocked ? (
                          <svg className="cdp-module-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                        ) : (
                          <svg className="cdp-module-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                        )}
                      </div>

                      {isExpanded && !isLocked && mod.lectures && mod.lectures.length > 0 && (
                        <div className="cdp-lectures">
                          {mod.lectures.map((lec) => (
                            <div
                              key={lec.id}
                              onClick={() => {
                                if (lec.type !== 'document') {
                                  setActiveLecture(lec);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                } else {
                                  openLectureDocument(lec);
                                }
                              }}
                              className={`cdp-lecture ${activeLecture?.id === lec.id ? 'active' : ''}`}
                              style={{ cursor: 'pointer', opacity: (isLocked && lec.id !== activeLecture?.id) ? 0.5 : 1 }}
                            >
                              <div className="cdp-lecture-left">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: activeLecture?.id === lec.id ? 'var(--cyan)' : 'inherit' }}>
                                  {lec.type === 'document' ? (
                                    <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></>
                                  ) : (
                                    <><circle cx="12" cy="12" r="10"></circle><polygon points="10 8 16 12 10 16 10 8"></polygon></>
                                  )}
                                </svg>
                                <span style={{ color: activeLecture?.id === lec.id ? 'var(--cyan)' : 'inherit', fontWeight: activeLecture?.id === lec.id ? 'bold' : 'normal' }}>
                                  {lec.title}
                                </span>
                              </div>
                              <span className="cdp-lecture-dur">{lec.duration}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </section>

            {/* Instructor */}
            <section className="cdp-section">
              <h2 className="cdp-section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Instructor
              </h2>
              <div className="cdp-instructor">
                <div className="cdp-instructor-img">
                  <img alt="Arnab Chatterjee" src="/assets/arnabchatterjee.jpg" />
                </div>
                <div className="cdp-instructor-info">
                  <h3>Arnab Chatterjee</h3>
                  <div className="cdp-role">Founder & CTO, ENGI&lt;mitra&gt;</div>
                  <p>
                    Arnab is the mastermind behind ENGImitra. He specializes in practical engineering applications and making complex topics easy to understand for first-year students.
                  </p>
                  <div className="cdp-instructor-links">
                    <a href="https://www.instagram.com/mainly_arnab__" target="_blank" rel="noreferrer">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                      Instagram
                    </a>
                    <a href="https://www.linkedin.com/in/arnab-chatterjee-prsnl/" target="_blank" rel="noreferrer">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* ---- Right Sidebar ---- */}
          <aside className="cdp-sidebar">
            <div className="cdp-pricing-card">
              {discount && <div className="cdp-discount-badge">{discount}% OFF</div>}
              <div className="cdp-price-row">
                <span className="cdp-price-current">₹{course.price}</span>
                {course.original_price && (
                  <span className="cdp-price-original">₹{course.original_price}</span>
                )}
              </div>
              {hasPurchased ? (
                <div className="ndp-owned-badge" style={{ marginBottom: '1rem' }}>
                  <span className="material-symbols-outlined">check_circle</span> Full Access Unlocked
                </div>
              ) : (
                <>
                  <p className="cdp-price-urgency">Limited time offer. Grab it now! </p>
                  <button className="cdp-buy-btn" onClick={handleAddToCart}>Buy Now</button>
                  <button className="cdp-cart-btn" onClick={handleAddToCart}>Add to Cart</button>
                </>
              )}

              <div className="cdp-includes">
                <h4>This course includes</h4>
                <ul>
                  <li>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    Topic Wise Videos
                  </li>
                  <li>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
                    Extremely High Quality Resources
                  </li>
                  <li>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
                    Full Semester Access
                  </li>
                  <li>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                    Access on Mobile & PC
                  </li>
                  <li>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    Guaranteed Grade increase or 100% Money back
                  </li>
                </ul>
              </div>

              <div className="cdp-actions">
                <button className="cdp-action-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
                  Share
                </button>
                <button className="cdp-action-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                  Wishlist
                </button>
                <button className="cdp-action-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>
                  Gift
                </button>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Footer */}
      <footer className="em-footer">
        <div className="fg">
          <div className="fb">
            <a href="/" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
            <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
          </div>
          <div className="fc">
            <h5>Platform</h5>
            <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><a href={l === 'Courses' ? '/courses' : `/#${l.toLowerCase()}`}>{l}</a></li>)}</ul>
          </div>
          <div className="fc">
            <h5>Community</h5>
            <ul>
              <li><a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer">Instagram</a></li>
              <li><a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer">Facebook</a></li>
              <li><a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer">WhatsApp Group</a></li>
            </ul>
          </div>
          <div className="fc">
            <h5>Legal</h5>
            <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="fb-bottom">
          <p>© 2026 ENGImitra. All rights reserved.</p>
          <div className="soc-row">
            <a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer" className="soc" aria-label="Instagram">ig</a>
            <a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer" className="soc" aria-label="Facebook">fb</a>
            <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" className="soc" aria-label="WhatsApp Group">wa</a>
          </div>
        </div>
      </footer>

      <FloatingCart />
    </div>
  );
}
